/**
 * Download-protection refusals, as crate-core puts them on the wire.
 *
 * crate-core refuses a protected download with the usual `{ code, message }` body plus a
 * few fields per code. Four of those codes cannot be answered with a plain error toast: one
 * asks the member to wait and then resumes by itself, the others have to block until the
 * member has answered. This module owns the knowledge of which codes those are and what shape
 * each one arrives in, so the download hook only asks "is this a refusal I should present?"
 * and never learns the wire format.
 *
 * Field names match the API contract in the crate-core PRD deliberately: one spelling for
 * the same fact, so a contract change shows up here and nowhere else.
 */

/** A `429 DOWNLOAD_DELAYED`: the download is throttled and becomes ready at `ready_at`. */
export type DownloadDelayedRefusal = {
  code: 'DOWNLOAD_DELAYED'
  /** Admin-edited text. Shown to the member as given. */
  message?: string
  /** ISO 8601 moment the download becomes ready. */
  ready_at?: string
  /** Seconds until the download becomes ready. Used when `ready_at` is absent or unreadable. */
  retry_after?: number
}

/** A `403 DOWNLOAD_COOLDOWN`: downloads are suspended for this member until `ends_at`. */
export type DownloadCooldownRefusal = {
  code: 'DOWNLOAD_COOLDOWN'
  /** Admin-edited text. Shown to the member as given. */
  message?: string
  /** ISO 8601 moment downloads resume. */
  ends_at?: string
  support_url?: string
}

/**
 * A `403 CHALLENGE_REQUIRED`: the member has to pass a Cloudflare Turnstile challenge before
 * this download is allowed. The same request is then sent again carrying the widget's token.
 */
export type ChallengeRequiredRefusal = {
  code: 'CHALLENGE_REQUIRED'
  /** Admin-edited text. Shown to the member as given. */
  message?: string
  /**
   * The Turnstile site key to render the widget with. It travels in the refusal rather than
   * in the webapp's configuration, so rotating it is a crate-core change alone.
   */
  site_key?: string
}

/**
 * A `403 UPSELL_REQUIRED`: the member is being offered an Enterprise plan, and downloads
 * stay refused until they say what they did with the offer.
 *
 * `penalty_id` is what the answer is reported against, so without one there is nothing to
 * record and nothing that would lift the refusal.
 */
export type UpsellRequiredRefusal = {
  code: 'UPSELL_REQUIRED'
  /** Admin-edited text. Shown to the member as given. */
  message?: string
  /** The penalty the outcome is recorded against. */
  penalty_id?: number
  /** Where the call to action sends the member. */
  cta_url?: string
}

export type DownloadRefusal =
  | DownloadDelayedRefusal
  | DownloadCooldownRefusal
  | ChallengeRequiredRefusal
  | UpsellRequiredRefusal

/**
 * The header a retried download carries the Turnstile token in. Named here beside the
 * refusal that asks for it, so the contract with crate-core has one spelling.
 */
export const TURNSTILE_TOKEN_HEADER = 'X-Turnstile-Token'

/**
 * The refusal codes this module presents, exported so the download hook's list of codes it
 * must not treat as a plain failure is spread from here rather than kept in step by hand.
 */
export const PRESENTED_REFUSAL_CODES: ReadonlySet<string> = new Set<DownloadRefusal['code']>([
  'DOWNLOAD_DELAYED',
  'DOWNLOAD_COOLDOWN',
  'CHALLENGE_REQUIRED',
  'UPSELL_REQUIRED',
])

/**
 * Reads a refusal out of an error response body, or returns null when the body is not one
 * of the refusals this module presents. Accepts an unknown value because it sits on the
 * boundary: the caller hands over whatever the failed request parsed to.
 */
export function parseDownloadRefusal(body: unknown): DownloadRefusal | null {
  if (!body || typeof body !== 'object') {
    return null
  }

  const code = (body as { code?: unknown }).code

  if (typeof code !== 'string' || !PRESENTED_REFUSAL_CODES.has(code)) {
    return null
  }

  return body as DownloadRefusal
}

/**
 * An ISO 8601 instant as epoch milliseconds, or null when the API sent nothing readable.
 * Both refusals carry one, and neither can trust it to be present.
 */
export function parseInstant(value?: string): number | null {
  if (!value) {
    return null
  }

  const milliseconds = Date.parse(value)

  return Number.isNaN(milliseconds) ? null : milliseconds
}

/**
 * Milliseconds to wait before a delayed download is ready, measured against the browser
 * clock at the moment the refusal arrived. Prefers `ready_at` and falls back to
 * `retry_after`; a moment already past means no wait at all rather than a negative one.
 */
export function delayUntilReadyMs(refusal: DownloadDelayedRefusal, now: number = Date.now()): number {
  const readyAt = parseInstant(refusal.ready_at)

  const milliseconds = readyAt === null ? (refusal.retry_after ?? 0) * 1000 : readyAt - now

  return Math.max(milliseconds, 0)
}

/**
 * Whether a download request is going to crate-core, and may therefore be made at all.
 *
 * Every such request carries the member's JWT, and a solved Turnstile token when there is
 * one. Both are credentials: sent anywhere else they are an account handed over and a
 * CAPTCHA-solving service run on our members. Some download URLs reach this code from a
 * query parameter rather than from our own routing, so the destination is checked rather
 * than trusted.
 *
 * Resolving against the API base is what the HTTP client itself does — an absolute URL is
 * used as given, a relative one hangs off the base — so this answers where the request will
 * actually land rather than where it looks like it might.
 */
export function targetsCrateCore(url: string, apiBaseUrl: string): boolean {
  try {
    return new URL(url, apiBaseUrl).origin === new URL(apiBaseUrl).origin
  } catch {
    return false
  }
}

/**
 * Whether a failed upsell outcome request in fact means crate-core already has the outcome.
 *
 * That is what a 409 says — a double press, or a second tab that answered first — and the
 * refusal is lifted either way, so treating it as a failure would put an error in front of
 * a member who did nothing wrong. Every other status leaves the refusal standing.
 *
 * It lives here with the rest of the wire contract, so a change to what crate-core answers
 * shows up in one place.
 */
export function isUpsellOutcomeAlreadyRecorded(status: number | undefined): boolean {
  return status === 409
}
